import React, { useMemo, useState } from 'react';
import { SearchIcon, DownloadIcon, Trash2Icon, CheckIcon, LinkIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { ImportRegistrations } from './ImportRegistrations';
import { useRegistry } from '../../contexts/RegistryContext';
import { formatWhen, toCsv } from '../../utils/adminFormat';
export function RegistrationsPanel() {
  const {
    registrations,
    toggleReviewed,
    removeRegistration,
    lastRefreshedAt
  } = useRegistry();
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return registrations;
    return registrations.filter(r => [r.fullName, r.email, r.professionalTitle, r.reference].some(v => v.toLowerCase().includes(q)));
  }, [query, registrations]);
  const exportCsv = () => {
    const csv = toCsv(registrations.map(r => ({
      Reference: r.reference,
      Name: r.fullName,
      Email: r.email,
      Title: r.professionalTitle,
      Years: r.yearsExperience,
      LinkedIn: r.linkedInUrl,
      Motivation: r.motivation,
      Submitted: r.submittedAt,
      Reviewed: r.reviewed ? 'Yes' : 'No'
    })));
    const url = URL.createObjectURL(new Blob([csv], {
      type: 'text/csv'
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `velnoris-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return <div data-id="element-358" data-mp="x17m3ca6href">
      <div className="flex flex-wrap items-center gap-3" data-id="element-359" data-mp="x0xtri1nv85k">
        <div className="relative min-w-[15rem] flex-1" data-id="element-360" data-mp="j2r6evuc8qk5">
          <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal-300" data-id="element-361" data-mp="n4uhhjd3ht4w" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} aria-label="Search registrations" placeholder="Search by name, email, title or reference" className="w-full border border-navy-100 bg-white py-2.5 pl-10 pr-3 font-sans text-[0.875rem] text-ink placeholder:text-charcoal-200 focus:border-navy-500" data-id="element-362" data-mp="33vujc4wo9zeh" />
        </div>
        <Button size="sm" variant="secondary" onClick={exportCsv} disabled={!registrations.length} data-id="element-363" data-mp="zo9oraigsakh">
          <DownloadIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-364" data-mp="vmo432zourzq" />
          Export CSV
        </Button>
      </div>

      <p className="mt-3 font-sans text-[0.75rem] text-charcoal-300" data-id="element-365" data-mp="qm0dujjaaf6k">
        {registrations.length} total · checked {formatWhen(lastRefreshedAt)}
      </p>

      <ImportRegistrations data-id="element-366" data-mp="govq4f9sibnn" />

      {!filtered.length ? <p className="mt-6 border border-dashed border-navy-200 bg-white p-10 text-center font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-367" data-mp="gqf604rjakow">
          {registrations.length ? 'No registrations match that search.' : 'No registrations yet. Submissions from the public form appear here — or add someone by hand above.'}
        </p> : <ul className="mt-6 divide-y divide-navy-100 border-y border-navy-100 bg-white" data-id="element-368" data-mp="2nkpo74vk8taf">
          {filtered.map(r => {
        const open = openId === r.id;
        return <li key={r.id} data-id="element-369" data-mp="2xbk7q1hf9es6">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5" data-id="element-370" data-mp="2xaso7p13n7i9">
                  <button type="button" onClick={() => setOpenId(open ? '' : r.id)} aria-expanded={open} className="min-w-0 flex-1 text-left" data-id="element-371" data-mp="20mcvjk2p0i0x">
                    <span className="flex flex-wrap items-center gap-2" data-id="element-372" data-mp="29o2kmj93r4vj">
                      <span className="font-serif text-[1.0625rem] text-ink" data-id="element-373" data-mp="2em45n9q39e9n">{r.fullName}</span>
                      {!r.reviewed ? <span className="border border-gold bg-gold-100 px-1.5 py-0.5 font-sans text-[0.625rem] uppercase tracking-[0.1em] text-gold-700" data-id="element-374" data-mp="3pzptmrk7p9tl">
                          New
                        </span> : null}
                    </span>
                    <span className="mt-0.5 block truncate font-sans text-[0.8125rem] text-charcoal-500" data-id="element-375" data-mp="xuiiqslim5d0">
                      {[r.professionalTitle, r.email].filter(Boolean).join(' · ')}
                    </span>
                    <span className="mt-0.5 block font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-376" data-mp="1lqego86r3aes">
                      {r.reference} · {formatWhen(r.submittedAt)}
                    </span>
                  </button>
                  <Button size="sm" variant="secondary" onClick={() => toggleReviewed(r.id)} data-id="element-377" data-mp="1uhn5ar307b02">
                    <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-378" data-mp="1qi5xvs40o3s9" />
                    {r.reviewed ? 'Reviewed' : 'Mark reviewed'}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => removeRegistration(r.id)} data-id="element-379" data-mp="1zhsxc8d6qzio">
                    <Trash2Icon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-380" data-mp="2j3l3fz32fqz5" />
                    <span className="sr-only" data-id="element-381" data-mp="2j1i42og0wlhr">Delete {r.fullName}</span>
                  </Button>
                </div>

                {open ? <dl className="grid gap-4 border-t border-navy-100 bg-parchment px-4 py-4 sm:grid-cols-2" data-id="element-382" data-mp="2966oy50pdc9h">
                    {[['Email', r.email], ['Professional title', r.professionalTitle || '—'], ['Years of experience', r.yearsExperience || '—']].map(([k, v]) => <div key={k} data-id="element-383" data-mp="255muj97ikmr1">
                        <dt className="font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-384" data-mp="1qfozc9b62wsc">{k}</dt>
                        <dd className="mt-1 font-sans text-[0.875rem] text-ink" data-id="element-385" data-mp="1ves5jx2vv91w">{v}</dd>
                      </div>)}
                    <div data-id="element-386" data-mp="1hglfv4838fdb">
                      <dt className="font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-387" data-mp="1lgcd9qs4dtqt">
                        LinkedIn
                      </dt>
                      <dd className="mt-1 font-sans text-[0.875rem] text-ink" data-id="element-388" data-mp="3o4ayy0oo538k">
                        {r.linkedInUrl ? <a href={r.linkedInUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 underline underline-offset-2" data-id="element-389" data-mp="3ec56yqyf2ltd">
                            <LinkIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-390" data-mp="5wd0u01mhy34" />
                            View profile
                          </a> : '—'}
                      </dd>
                    </div>
                    <div className="sm:col-span-2" data-id="element-391" data-mp="2lnr4nz0ap3c0">
                      <dt className="font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-392" data-mp="2lpc1xwdzv0dk">
                        Why they want to work with us
                      </dt>
                      <dd className="mt-1 max-w-2xl border-l border-navy-100 pl-3 font-serif text-[0.9375rem] leading-relaxed text-charcoal-700" data-id="element-393" data-mp="x5y6tsdknyfx">
                        {r.motivation || '—'}
                      </dd>
                    </div>
                  </dl> : null}
              </li>;
      })}
        </ul>}
    </div>;
}